import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import redflag from "../../../../assets/images/bahrainsmlogo.svg";
import { ReactComponent as MacbookIcon } from "../../../../assets/images/devices/laptop_chromebook.svg";
import { ReactComponent as SmartphoneIcon } from "../../../../assets/images/devices/phone_android.svg";
import { ReactComponent as TabletIcon } from "../../../../assets/images/devices/tablet_android.svg";
import { ReactComponent as SmartwatchIcon } from "../../../../assets/images/devices/watch.svg";
import { getLoggedInUserDataFromLocalStorage } from "../../../../config/config";
import useLanguageTranslate from "../../../../hooks/useLanguageTranslate";
import useSmartDeviceFlow from "../../../../hooks/useSmartDeviceFlow";
import useSmartDeviceFormData from "../../../../hooks/useSmartDeviceFormData";
import { DEVICE_TYPES } from "../../../../pages/smartDeviceInsurancePages/newSmartDevicePolicy/data";
import {
  selectLoginTokenInfo,
  setLoginTokenInfo,
} from "../../../../redux/features/loginRegisterSlice";
import { selectSmartDeviceForm } from "../../../../redux/features/smartDeviceFormSlice";
import FileUpload from "../../../common/fileUploadInput/fileUploadInput";
import Input from "../../../common/Input/Input";
import SingleDatePicker from "../../../common/SingleDatePicker/SingleDatePicker";
import BottomStickyBtnLayout from "../../layouts/BottomStickyBtnLayout";
import SmartDeviceJourneyLayout from "../../layouts/SmartDeviceJourneyLayout";
import SmartDeviceLoginPromptModal from "../../modals/smartDeviceLoginPromptModal";
import { SMART_DEVICE_SELECTION_FORM } from "../deviceSelection/deviceSelection";

// Form constant
export const SMART_DEVICE_PURCHASE_DETAILS_FORM =
  "SMART_DEVICE_PURCHASE_DETAILS_FORM";

const PurchaseDetails = ({ onClose }) => {
  const { handleNextSubstep, handlePrevStep } = useSmartDeviceFlow();
  const translate = useLanguageTranslate();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const loginTokenInfo = useSelector(selectLoginTokenInfo);
  const allFormData = useSelector(selectSmartDeviceForm);

  // Get device data from previous step
  const deviceData = allFormData?.[SMART_DEVICE_SELECTION_FORM] || {};

  // State management
  const [formData, setFormData] = useState({
    purchaseReceipt: null,
    devicePrice: "",
    purchaseDate: "",
    premium: 147.6, // Mock premium - will be calculated from API
  });
  const [errors, setErrors] = useState({});
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showSummary, setShowSummary] = useState(false);
  const [isWaitingForLogin, setIsWaitingForLogin] = useState(false);

  // Redux form data persistence
  const { saveSmartDeviceDataInRedux, redux_form_data } =
    useSmartDeviceFormData(SMART_DEVICE_PURCHASE_DETAILS_FORM, setFormData);

  // Load saved data on mount
  useEffect(() => {
    if (redux_form_data) {
      setFormData(redux_form_data);
    }
  }, []);

  // Multiple login detection methods (copied from Personal Cyber Insurance)

  // Method 1: Watch for loginTokenInfo changes
  useEffect(() => {
    if (isWaitingForLogin && loginTokenInfo) {
      console.log("✅ Login detected via token change, proceeding to next step");
      setIsWaitingForLogin(false);
      setShowLoginModal(false);
      saveSmartDeviceDataInRedux(formData);
      handleNextSubstep();
    }
  }, [loginTokenInfo, isWaitingForLogin, formData, saveSmartDeviceDataInRedux, handleNextSubstep]);

  // Method 2: Additional effect to handle when user logs in from outside
  useEffect(() => {
    const isLoggedIn = Boolean(loginTokenInfo);
    if (isLoggedIn && showLoginModal && isWaitingForLogin) {
      console.log("✅ Login detected via isLoggedIn change, proceeding to next step");
      setShowLoginModal(false);
      setIsWaitingForLogin(false);
      saveSmartDeviceDataInRedux(formData);
      handleNextSubstep();
    }
  }, [loginTokenInfo, showLoginModal, isWaitingForLogin, formData, saveSmartDeviceDataInRedux, handleNextSubstep]);

  // Method 3: Listen for storage changes to detect login from popup window
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === "user" || e.key === "loginToken") {
        console.log("✅ Storage change detected, checking for login");
        const tokenInfo = getLoggedInUserDataFromLocalStorage();
        if (tokenInfo) {
          dispatch(setLoginTokenInfo(tokenInfo));
          setIsWaitingForLogin(false);
          setShowLoginModal(false);
          saveSmartDeviceDataInRedux(formData);
          handleNextSubstep();
        }
      }
    };

    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, [dispatch, formData, saveSmartDeviceDataInRedux, handleNextSubstep]);

  // Method 4: Polling mechanism as fallback
  useEffect(() => {
    if (!isWaitingForLogin) return;

    const pollForLogin = setInterval(() => {
      const tokenInfo = getLoggedInUserDataFromLocalStorage();
      if (tokenInfo) {
        console.log("✅ Login detected via polling, proceeding to next step");
        dispatch(setLoginTokenInfo(tokenInfo));
        setIsWaitingForLogin(false);
        setShowLoginModal(false);
        saveSmartDeviceDataInRedux(formData);
        handleNextSubstep();
        clearInterval(pollForLogin);
      }
    }, 1000);

    return () => clearInterval(pollForLogin);
  }, [isWaitingForLogin, dispatch, formData, saveSmartDeviceDataInRedux, handleNextSubstep]);

  // Removed auto-skip logic - Purchase Details page should always be shown
  // Users need to enter purchase details regardless of login status

  // Get device icon component based on type
  const getDeviceIconComponent = () => {
    switch (deviceData.deviceType) {
      case DEVICE_TYPES.SMARTPHONE:
        return SmartphoneIcon;
      case DEVICE_TYPES.TABLET:
        return TabletIcon;
      case DEVICE_TYPES.SMARTWATCH:
        return SmartwatchIcon;
      case DEVICE_TYPES.MACBOOK:
        return MacbookIcon;
      default:
        return SmartphoneIcon;
    }
  };

  // Get device display name
  const getDeviceDisplayName = () => {
    if (deviceData.brand && deviceData.model) {
      return `${deviceData.brand} ${deviceData.model}`;
    }
    return translate("Device", "الجهاز");
  };

  // Handle form field changes
  const handleFormDataChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    // Clear error for this field when user makes changes
    setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  // Handle date change
  const handleDateChange = (date) => {
    handleFormDataChange("purchaseDate", date);
  };

  // Handle purchase receipt upload
  const handleReceiptUpload = (
    uploaded,
    fileName,
    fileData = null,
    fileSize = null
  ) => {
    if (uploaded) {
      handleFormDataChange("purchaseReceipt", {
        uploaded: true,
        fileName,
        fileSize: fileData?.fileSize,
        file: fileData?.file,
      });
    } else {
      handleFormDataChange("purchaseReceipt", null);
    }
  };

  // Handle change device button
  const handleChangeDevice = () => {
    handlePrevStep();
  };

  // Validation
  const validateForm = () => {
    const newErrors = {};

    // Purchase receipt is required
    if (!formData.purchaseReceipt || !formData.purchaseReceipt.uploaded) {
      newErrors.purchaseReceipt = translate(
        "This field is required.",
        "هذا الحقل مطلوب."
      );
    }

    // Device price is required
    if (
      !formData.devicePrice ||
      formData.devicePrice.toString().trim() === "" ||
      parseFloat(formData.devicePrice.toString().replace(/[,\s]/g, "")) === 0
    ) {
      newErrors.devicePrice = translate(
        "This field is required.",
        "هذا الحقل مطلوب."
      );
    } else {
      // Remove commas and spaces before parsing to handle formatted numbers like "1,000" or "1 000"
      const price = parseFloat(
        formData.devicePrice.toString().replace(/[,\s]/g, "")
      );
      if (isNaN(price) || price < 0) {
        newErrors.devicePrice = translate(
          "Please enter a valid price.",
          "الرجاء إدخال سعر صالح."
        );
      } else if (price > 800) {
        newErrors.devicePrice = translate(
          "Device price must not exceed BHD 800.",
          "يجب ألا يتجاوز سعر الجهاز 800 د.ب."
        );
      }
    }

    // Purchase date is required
    if (!formData.purchaseDate) {
      newErrors.purchaseDate = translate(
        "This field is required.",
        "هذا الحقل مطلوب."
      );
    } else {
      // Check if purchase date is not in the future
      const selectedDate = new Date(formData.purchaseDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      if (selectedDate > today) {
        newErrors.purchaseDate = translate(
          "Purchase date cannot be in the future.",
          "تاريخ الشراء لا يمكن أن يكون في المستقبل."
        );
      }

      // Check if device is not older than 30 days
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      thirtyDaysAgo.setHours(0, 0, 0, 0); // Start of that day

      if (selectedDate < thirtyDaysAgo) {
        newErrors.purchaseDate = translate(
          "Device must be purchased within the last 30 days.",
          "يجب شراء الجهاز خلال الـ 30 يومًا الماضية."
        );
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle continue
  const handleContinue = () => {
    // Run validation and get the errors
    const newErrors = {};

    // Purchase receipt is required
    if (!formData.purchaseReceipt || !formData.purchaseReceipt.uploaded) {
      newErrors.purchaseReceipt = translate(
        "This field is required.",
        "هذا الحقل مطلوب."
      );
    }

    // Device price is required
    if (
      !formData.devicePrice ||
      formData.devicePrice.toString().trim() === "" ||
      parseFloat(formData.devicePrice.toString().replace(/[,\s]/g, "")) === 0
    ) {
      newErrors.devicePrice = translate(
        "This field is required.",
        "هذا الحقل مطلوب."
      );
    } else {
      // Remove commas and spaces before parsing to handle formatted numbers like "1,000" or "1 000"
      const price = parseFloat(
        formData.devicePrice.toString().replace(/[,\s]/g, "")
      );
      if (isNaN(price) || price < 0) {
        newErrors.devicePrice = translate(
          "Please enter a valid price.",
          "الرجاء إدخال سعر صالح."
        );
      } else if (price > 800) {
        newErrors.devicePrice = translate(
          "Device price must not exceed BHD 800.",
          "يجب ألا يتجاوز سعر الجهاز 800 د.ب."
        );
      }
    }

    // Purchase date is required
    if (!formData.purchaseDate) {
      newErrors.purchaseDate = translate(
        "This field is required.",
        "هذا الحقل مطلوب."
      );
    } else {
      // Check if purchase date is not in the future
      const selectedDate = new Date(formData.purchaseDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      if (selectedDate > today) {
        newErrors.purchaseDate = translate(
          "Purchase date cannot be in the future.",
          "تاريخ الشراء لا يمكن أن يكون في المستقبل."
        );
      }

      // Check if device is not older than 30 days
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      thirtyDaysAgo.setHours(0, 0, 0, 0); // Start of that day

      if (selectedDate < thirtyDaysAgo) {
        newErrors.purchaseDate = translate(
          "Device must be purchased within the last 30 days.",
          "يجب شراء الجهاز خلال الـ 30 يومًا الماضية."
        );
      }
    }

    // Set errors
    setErrors(newErrors);

    // If no errors, proceed
    if (Object.keys(newErrors).length === 0) {
      // Check if user is logged in
      if (!loginTokenInfo) {
        // Show login modal for guest users (DON'T save to Redux yet - causes re-render)
        console.log("🔐 Guest user - showing login modal");
        setShowLoginModal(true);
        setIsWaitingForLogin(true); // Flag that we're waiting for login
      } else {
        // Save form data to Redux for logged in users
        saveSmartDeviceDataInRedux(formData);
        // User is logged in, proceed to next step directly
        handleNextSubstep();
      }
    } else {
      // Scroll to first error - use the first field from form order
      const fieldOrder = ["purchaseReceipt", "devicePrice", "purchaseDate"];
      const firstErrorField = fieldOrder.find((field) => newErrors[field]);

      if (firstErrorField) {
        // Map field names to actual DOM element identifiers
        const elementMap = {
          purchaseReceipt: "purchase_receipt",
          devicePrice: "devicePrice",
          purchaseDate: "purchaseDate",
        };

        const elementId = elementMap[firstErrorField];
        const errorElement =
          document.querySelector(`[name="${elementId}"]`) ||
          document.getElementById(elementId) ||
          document.querySelector(".form-input");

        errorElement?.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }
  };

  // Handle login success
  const handleLoginSuccess = () => {
    console.log("✅ handleLoginSuccess called");
    // The useEffect watching loginTokenInfo will handle the navigation
    // This is just a fallback in case the useEffect doesn't trigger
    setIsWaitingForLogin(false);
    setShowLoginModal(false);
    saveSmartDeviceDataInRedux(formData);
    handleNextSubstep();
  };

  // Handle skip login
  const handleSkipLogin = () => {
    console.log("⏭️ User skipped login");
    // Close modal and proceed as guest
    setShowLoginModal(false);
    setIsWaitingForLogin(false);

    // Save form data to Redux
    saveSmartDeviceDataInRedux(formData);

    // Proceed to next step
    handleNextSubstep();
  };

  // Handle show summary
  const handleShowSummary = () => {
    setShowSummary(!showSummary);
    // TODO: Implement summary modal
  };

  // Determine if continue button should be disabled
  const hasReceipt =
    formData.purchaseReceipt && formData.purchaseReceipt.uploaded;
  const hasPrice =
    formData.devicePrice && formData.devicePrice.toString().trim() !== "";
  const hasDate = formData.purchaseDate && formData.purchaseDate !== "";

  const isContinueDisabled = !hasReceipt || !hasPrice || !hasDate;

  return (
    <>
      <SmartDeviceJourneyLayout is_hide_back_btn={false}>
        <BottomStickyBtnLayout
          btn_onClick={handleContinue}
          btn_label={translate("Continue", "التالي")}
          btn_disabled={isContinueDisabled}
          show_summary={true}
          summary_onClick={handleShowSummary}
          summary_label={translate("Show Summary", "عرض التسعيرة")}
          premium={formData.premium}
          premium_label={translate("BHD", "د.ب")}
        >
          <div className="w-[33%] max767:w-full mx-auto ipad:w-[65%] pb-5 max767:px-4">
            <h1 className="text-[#1A2024] text-center text-2xl not-italic font-semibold leading-8 tracking-[-0.24px] max767:text-left  max767:rtl:text-right mb-2">
              {translate("Purchase Details", "حول جهازك")}
            </h1>
            <p className="text-[#1A2024] text-center text-base not-italic font-normal leading-6 tracking-[-0.16px] mb-6 max767:text-left  max767:rtl:text-right">
              {translate(
                "Please enter the required information below.",
                "يرجى إدخال المعلومات المطلوبة أدناه."
              )}
            </p>

            {/* Selected Device Display */}
            <div className="bg-[#F6F8F9] max767:border max767:border-[#DDE2E4] max767:bg-white rounded-lg p-4 max767:p-2 mb-6 flex items-center justify-between">
              <div className="flex items-center gap-3">
                {(() => {
                  const IconComponent = getDeviceIconComponent();
                  return (
                    <div className="w-10 h-10 max767:size-16 flex items-center justify-center text-[#48535B] max767:bg-[#F6F8F9]">
                      <IconComponent className="w-8 h-8" />
                    </div>
                  );
                })()}
              </div>
              <div className="flex items-center w-full justify-between ml-4 max767:rtl:mr-4 max767:flex-col gap-1 max767:items-start">
                <span className="text-[#1A2024] text-base font-semibold">
                  {getDeviceDisplayName()}
                </span>
                <button
                  onClick={handleChangeDevice}
                  className="inline-flex items-center text-[#1A2024] bg-white hover:bg-gray-50 text-sm font-semibold px-4 py-2 rounded-lg border border-[#E5E7EB] transition-colors max767:rtl:flex-row-reverse max767:rtl:gap-2"
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    className="mr-2 rtl:ml-2 rtl:mr-0"
                  >
                    <path
                      d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"
                      fill="currentColor"
                    />
                  </svg>
                  {translate("Change Device", "تغيير الجهاز")}
                </button>
              </div>
            </div>

            {/* Device purchase receipt */}
            <div className="mb-6 flex flex-col" name="purchase_receipt">
              <label className="label text-[#48535B] text-base not-italic font-normal leading-[26px] tracking-[-0.16px] !mb-[5px]">
                {translate("Device purchase receipt", "وثيقة شراء الجهاز")}
              </label>
              <FileUpload
                name="purchase_receipt"
                toggleFileUpload={handleReceiptUpload}
                uploadedFile={formData.purchaseReceipt}
              />
              {errors.purchaseReceipt && (
                <div className="mt-2 text-red-600 text-sm">
                  {errors.purchaseReceipt}
                </div>
              )}
            </div>

            {/* Device price */}
            <div className="mb-6">
              <Input
                endAdornment={
                  <div className="flex align-middle mt-1">
                    <img src={redflag} alt="flag" />
                    <p className="ml-2 text-black text-lg not-italic font-normal leading-[26px] tracking-[-0.18px] rtl:mr-2 rtl:ml-0">
                      {translate("BHD", "د.ب")}
                    </p>
                  </div>
                }
                type="text"
                formatNumber={true}
                name="devicePrice"
                label={translate("Device price", "سعر الجهاز")}
                value={formData.devicePrice}
                handleChange={(e) =>
                  handleFormDataChange("devicePrice", e.target.value)
                }
                error={errors.devicePrice}
              />
            </div>

            {/* Purchase date */}
            <div className="mb-6">
              <SingleDatePicker
                name="purchaseDate"
                label={translate("Purchase date", "تاريخ الشراء")}
                value={formData.purchaseDate}
                onChange={handleDateChange}
                error={errors.purchaseDate}
                placeholder={translate(
                  "Select purchase date",
                  "اختر تاريخ الشراء"
                )}
                maxDate={(() => {
                  const today = new Date();
                  today.setHours(23, 59, 59, 999); // End of today
                  return today;
                })()} // Cannot select future dates
                minDate={(() => {
                  // Calculate the minimum valid date (30 days ago + 1 day)
                  // If today is 12/12, validation allows Nov 13 and newer
                  const today = new Date();
                  const thirtyDaysAgo = new Date(today);
                  thirtyDaysAgo.setDate(today.getDate() - 30);
                  thirtyDaysAgo.setHours(0, 0, 0, 0);
                  // Add 1 day because validation check is selectedDate < thirtyDaysAgo (invalid)
                  // So minimum valid date is thirtyDaysAgo + 1
                  const minValidDate = new Date(thirtyDaysAgo);
                  minValidDate.setDate(thirtyDaysAgo.getDate() + 1);
                  return minValidDate;
                })()} // Cannot select dates older than 30 days
              />
            </div>
          </div>
        </BottomStickyBtnLayout>
      </SmartDeviceJourneyLayout>

      {/* Login Prompt Modal */}
      <SmartDeviceLoginPromptModal
        show={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        onLoginSuccess={handleLoginSuccess}
        onSkip={handleSkipLogin}
      />
    </>
  );
};

export default PurchaseDetails;
